import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FiX, FiPlus, FiTrash2, FiAlertTriangle, FiMapPin, FiInfo } from 'react-icons/fi';
import Button from '../ui/Button';
import { useQuotesBookings } from './QuotesBookingsContext';
import { LoadContext } from '../loadContext';
import LocationSelector from './LocationSelector';
import StopsTable from './StopsTable';
import { composeDockHoursRange, splitDockHoursRange, normalizeTime } from '../../utils/time';
import styles from './CreateShipmentDrawer.module.css';
import drawerStyles from './Drawer.module.css';

const modes = ['FTL', 'LTL'];
const valueBuckets = [
  '$0 - $25k',
  '$25k - $50k',
  '$50k - $75k',
  '$75k - $100k',
  '$100k - $125k',
  '$125k - $150k',
  '$150k+'
];

const unitOptions = [
  'Bags',
  'Bales',
  'Barrels',
  'Base Units',
  'Bin',
  'Blocks',
  'Bottles',
  'Boxes',
  'Buckets',
  'Bulk',
  'Bundles',
  'Bushels',
  'Cans',
  'Carboys',
  'Carpets',
  'Carton',
  'Cases',
  'Coils',
  'Combinations',
  'Containers',
  'Crates',
  'Cylinders',
  'Drums',
  'Each',
  'Feet',
  'Gallons',
  'Grams',
  'Hundredweight (Net)',
  'Inner Pack',
  'Kegs',
  'Kilograms',
  'Kit',
  'Layer',
  'Pounds',
  'Liters',
  'Loose',
  'Metric Tons',
  'Other',
  'Pack',
  'Packages',
  'Pails',
  'Pair',
  'Pallets',
  'Pieces',
  'Pouches',
  'Racks',
  'Ream',
  'Reels',
  'Rolls',
  'Skids',
  'Sack',
  'Spool',
  'Tons',
  'Totes',
  'Trays',
  'Truckload',
  'Tubes / Pipes',
  'Tubs',
  'Units',
  'Vehicles'
];

const requirementDescriptions = {
  None: 'No special requirements.',
  Hazmat: 'Special handling and documentation for hazardous materials.',
  Oversize: 'For cargo that exceeds standard trailer dimensions.',
  '4ft Tarps': '4ft high tarps for protecting your load.',
  '6ft Tarps': '6ft high tarps for protecting your load.',
  '8ft Tarps': '8ft high tarps for protecting your load.',
  Dunnage: 'Extra material for securing and protecting your cargo.',
  'Coil Racks': 'Racks to securely transport coil products.',
  Lumber: 'Additional wood to stabilize and secure your cargo.',
  Chains: 'Heavy-duty chains for securing your load.',
  'Vented Trailer': 'Trailer with ventilation for airflow.',
  'Food Grade Trailer': 'Clean trailer suitable for food products.',
  'Extra Straps (3+)': 'More than 3 straps for extra load security.',
  'Security Plates': 'Plates securing cargo to floor of trailer.',
  'E-tracks': 'Adjustable tracks for securing different cargo sizes.',
  'Team Drivers': 'Two drivers for faster and continuous delivery.',
  Liftgate: 'Trailer with liftgate for loading/unloading heavy items.',
  'Pallet Jack': 'Manual tool provided for moving pallets.',
  'Blind Pickup': 'Pickup without disclosing shipper details.',
  'Blind Drop-off': 'Delivery without disclosing receiver details.',
  'Blind Pick/Drop': 'Pickup and delivery without disclosing shipper or receiver details.',
  'Download Temp Logs': 'Provide temperature recorder data after delivery.',
  'Multi-Temp Zones': 'Trailer with multiple temperature-controlled zones.',
  'Multi Pick/Drop': 'Supports complex routes with multiple stops.',
  'Escort Required': 'Pilot car or escort required for safe transit.',
  Ramps: 'Include ramp kit for drive-on cargo.',
  'Flag Kit': 'High-visibility flags and signage for oversized freight.',
  'Police Escort': 'Coordinate law enforcement escorts as required.',
  'Full Enclosure': 'Rolling tarp system to protect flatbed freight.',
  Straps: 'Supplemental straps for hotshot equipment.',
  'Logistics Posts': 'Logistics posts for strap placement in dry vans.',
  'Load Bars': 'Adjustable load bars for sprinter vans.',
  'Insurance Waiver': 'Additional insurance documentation provided.',
  'Liftgate Delivery': 'Liftgate required at delivery.',
  'Liftgate Pickup': 'Liftgate required at pickup.'
};

const specialRequirementsByEquipment = {
  'Dry Van': [
    'None',
    'Hazmat',
    'Dunnage',
    'Vented Trailer',
    'Food Grade Trailer',
    'Extra Straps (3+)',
    'Security Plates',
    'E-tracks',
    'Team Drivers',
    'Liftgate',
    'Pallet Jack',
    'Blind Pickup',
    'Blind Drop-off',
    'Blind Pick/Drop'
  ],
  Reefer: [
    'None',
    'Hazmat',
    'Dunnage',
    'Food Grade Trailer',
    'Extra Straps (3+)',
    'Security Plates',
    'E-tracks',
    'Team Drivers',
    'Liftgate',
    'Pallet Jack',
    'Blind Pickup',
    'Blind Drop-off',
    'Blind Pick/Drop',
    'Download Temp Logs'
  ],
  Flatbed: [
    'None',
    'Hazmat',
    'Oversize',
    '4ft Tarps',
    '6ft Tarps',
    '8ft Tarps',
    'Dunnage',
    'Coil Racks',
    'Lumber',
    'Chains',
    'Extra Straps (3+)',
    'Team Drivers',
    'Blind Pickup',
    'Blind Drop-off',
    'Blind Pick/Drop'
  ],
  Conestoga: [
    'None',
    'Hazmat',
    'Oversize',
    '4ft Tarps',
    '6ft Tarps',
    '8ft Tarps',
    'Dunnage',
    'Coil Racks',
    'Lumber',
    'Chains',
    'Extra Straps (3+)',
    'Team Drivers',
    'Blind Pickup',
    'Blind Drop-off',
    'Blind Pick/Drop',
    'Full Enclosure'
  ],
  'SD Conestoga': [
    'None',
    'Hazmat',
    'Oversize',
    '4ft Tarps',
    '6ft Tarps',
    '8ft Tarps',
    'Dunnage',
    'Coil Racks',
    'Lumber',
    'Chains',
    'Extra Straps (3+)',
    'Team Drivers',
    'Blind Pickup',
    'Blind Drop-off',
    'Blind Pick/Drop'
  ],
  RGN: [
    'None',
    'Hazmat',
    'Oversize',
    'Dunnage',
    'Chains',
    'Extra Straps (3+)',
    'Team Drivers',
    'Multi Pick/Drop',
    'Blind Pickup',
    'Blind Drop-off',
    'Blind Pick/Drop',
    'Escort Required',
    'Ramps'
  ],
  Lowboy: [
    'None',
    'Hazmat',
    'Oversize',
    'Dunnage',
    'Chains',
    'Extra Straps (3+)',
    'Team Drivers',
    'Blind Pickup',
    'Blind Drop-off',
    'Blind Pick/Drop',
    'Escort Required',
    'Flag Kit',
    'Police Escort'
  ],
  'Box Truck': [
    'None',
    'Hazmat',
    'Dunnage',
    'Food Grade Trailer',
    'Extra Straps (3+)',
    'Security Plates',
    'E-tracks',
    'Team Drivers',
    'Liftgate',
    'Pallet Jack',
    'Blind Pickup',
    'Blind Drop-off',
    'Blind Pick/Drop'
  ],
  'Sprinter Van': [
    'None',
    'Hazmat',
    'Dunnage',
    'Extra Straps (3+)',
    'Security Plates',
    'E-tracks',
    'Team Drivers',
    'Liftgate',
    'Pallet Jack',
    'Blind Pickup',
    'Blind Drop-off',
    'Blind Pick/Drop',
    'Load Bars',
    'Insurance Waiver'
  ],
  'Step Deck': [
    'None',
    'Hazmat',
    'Oversize',
    '4ft Tarps',
    '6ft Tarps',
    '8ft Tarps',
    'Dunnage',
    'Coil Racks',
    'Lumber',
    'Chains',
    'Extra Straps (3+)',
    'Team Drivers',
    'Blind Pickup',
    'Blind Drop-off',
    'Blind Pick/Drop'
  ],
  Tanker: [
    'None',
    'Hazmat',
    'Food Grade Trailer',
    'Extra Straps (3+)',
    'Team Drivers',
    'Blind Pickup',
    'Blind Drop-off',
    'Blind Pick/Drop'
  ],
  'Curtain Side': [
    'None',
    'Hazmat',
    'Oversize',
    'Dunnage',
    'Coil Racks',
    'Lumber',
    'Chains',
    'Extra Straps (3+)',
    'Team Drivers',
    'Blind Pickup',
    'Blind Drop-off',
    'Blind Pick/Drop'
  ],
  'Hotshot Trailer': [
    'None',
    'Hazmat',
    'Oversize',
    'Dunnage',
    'Chains',
    'Extra Straps (3+)',
    'Team Drivers',
    'Blind Pickup',
    'Blind Drop-off',
    'Blind Pick/Drop',
    'Straps'
  ],
  'Flatbed Hotshot': [
    'None',
    'Hazmat',
    'Oversize',
    'Dunnage',
    'Chains',
    'Extra Straps (3+)',
    'Team Drivers',
    'Blind Pickup',
    'Blind Drop-off',
    'Blind Pick/Drop',
    'Straps'
  ],
  Chassis: [
    'None',
    'Hazmat',
    'Dunnage',
    'Extra Straps (3+)',
    'Team Drivers',
    'Blind Pickup',
    'Blind Drop-off',
    'Blind Pick/Drop'
  ]
};

const createStop = (type = 'Pickup') => ({
  clientId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  type,
  location: null,
  appointmentType: '',
  date: '',
  windowStart: '',
  windowEnd: '',
  needsAppointmentScheduled: false,
  reference: '',
  appointmentNumber: '',
  notes: ''
});

const createCommodity = () => ({
  quantity: '',
  unit: '',
  description: ''
});

const CreateShipmentDrawer = ({ open, onClose }) => {
  const { addBooking, carriers, trailerDefaults } = useQuotesBookings();
  const { addLoad } = useContext(LoadContext);
  const [mode, setMode] = useState('FTL');
  const [equipment, setEquipment] = useState('Dry Van');
  const [length, setLength] = useState("53'");
  const [weight, setWeight] = useState('');
  const [valueRange, setValueRange] = useState(valueBuckets[0]);
  const [refNumber, setRefNumber] = useState('');
  const [commodities, setCommodities] = useState([]);
  const [specialRequirements, setSpecialRequirements] = useState([]);
  const [requirementsOpen, setRequirementsOpen] = useState(false);
  const requirementsRef = useRef(null);
  const [carrierId, setCarrierId] = useState(carriers[0]?.id || '');
  const [billRate, setBillRate] = useState('');
  const [billRateError, setBillRateError] = useState(false);
  const [stops, setStops] = useState([]);
  const [unitListId] = useState(() => `unit-options-${Math.random().toString(36).slice(2)}`);
  const [submitError, setSubmitError] = useState('');

  const lengthOptions = trailerDefaults[equipment]?.lengths || ["53'"];
  const equipmentSpecificRequirements = trailerDefaults[equipment]?.requirements || [];
  const catalogRequirements = specialRequirementsByEquipment[equipment] || [];

  const availableRequirements = useMemo(() => {
    const seen = new Set();
    const merged = [...catalogRequirements, ...equipmentSpecificRequirements];
    return merged.filter((req) => {
      if (seen.has(req)) {
        return false;
      }
      seen.add(req);
      return true;
    });
  }, [catalogRequirements, equipmentSpecificRequirements]);

  useEffect(() => {
    if (!lengthOptions.includes(length)) {
      setLength(lengthOptions[0]);
    }
  }, [lengthOptions, length]);

  useEffect(() => {
    setSpecialRequirements((prev) => prev.filter((req) => availableRequirements.includes(req)));
  }, [availableRequirements]);

  useEffect(() => {
    if (!requirementsOpen) {
      return undefined;
    }
    const handleClickOutside = (event) => {
      if (requirementsRef.current && !requirementsRef.current.contains(event.target)) {
        setRequirementsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [requirementsOpen]);

  useEffect(() => {
    setCarrierId((prev) => prev || carriers[0]?.id || '');
  }, [carriers]);

  if (!open) {
    return null;
  }

  const toggleRequirement = (requirement) => {
    setSpecialRequirements((prev) => {
      if (requirement === 'None') {
        return prev.includes('None') ? [] : ['None'];
      }
      const withoutNone = prev.filter((item) => item !== 'None');
      if (withoutNone.includes(requirement)) {
        return withoutNone.filter((item) => item !== requirement);
      }
      return [...withoutNone, requirement];
    });
  };

  const handleStopUpdate = (index, patch) => {
    setStops((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const handleTimeBlur = (index, field, value) => {
    handleStopUpdate(index, { [field]: normalizeTime(value) });
  };

  const resolveDockHours = (location) => {
    if (!location) return { from: '', to: '' };
    if (location.dockHoursFrom || location.dockHoursTo) {
      return {
        from: normalizeTime(location.dockHoursFrom || ''),
        to: normalizeTime(location.dockHoursTo || ''),
      };
    }
    const range = splitDockHoursRange(location.dockHours || '');
    return { from: normalizeTime(range.from || ''), to: normalizeTime(range.to || '') };
  };

  const handleLocationChange = (index, location) => {
    const currentStop = stops[index] || {};
    const patch = { location };
    const dockType = (location?.dockType || '').toUpperCase();
    if (dockType === 'FCFS') {
      const { from, to } = resolveDockHours(location);
      if (from && !currentStop.windowStart) {
        patch.windowStart = from;
      }
      if (to && !currentStop.windowEnd) {
        patch.windowEnd = to;
      }
    }
    handleStopUpdate(index, patch);
  };

  const handleAddStop = () => {
    setStops((prev) => [...prev, createStop('Pickup')]);
  };

  const handleRemoveStop = (index) => {
    setStops((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleReorderStops = (reordered) => {
    setStops(reordered);
  };

  const handleAddCommodity = () => {
    setCommodities((prev) => [...prev, createCommodity()]);
  };

  const handleCommodityChange = (index, patch) => {
    setCommodities((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const handleCommodityUnitBlur = (index, value) => {
    const resolved = unitOptions.find((option) => option.toLowerCase() === value.toLowerCase());
    handleCommodityChange(index, { unit: resolved || '' });
  };

  const handleRemoveCommodity = (index) => {
    setCommodities((prev) => prev.filter((_, idx) => idx !== index));
  };

  const resolveCarrier = carriers.find((carrier) => carrier.id === carrierId);
  const origin =
    stops.find((stop) => (stop.type || '').toLowerCase() === 'pickup')?.location || stops[0]?.location;
  const destination =
    [...stops]
      .reverse()
      .find((stop) => (stop.type || '').toLowerCase() === 'delivery')?.location || stops[stops.length - 1]?.location;
  const hasRouteContext = Boolean(origin && destination);
  const commoditySummary = commodities
    .filter((item) => item.quantity && item.unit && item.description)
    .map((item) => `${item.quantity} ${item.unit} ${item.description}`);

  const ensureCompleteLocation = (stop) => {
    if (!stop.location) {
      return false;
    }
    const { street, address, city, state: locationState, zip } = stop.location;
    const streetLine = street || address;
    return Boolean(streetLine && city && locationState && zip);
  };

  const combineDateTime = (date, time) => {
    if (!date) {
      return null;
    }
    const normalizedTime = time ? `${time.padStart(5, '0')}:00` : '00:00:00';
    const iso = new Date(`${date}T${normalizedTime}`);
    return Number.isNaN(iso.getTime()) ? null : iso.toISOString();
  };

  const buildStopPayload = (stop, sequence) => {
    const appointmentType = stop.appointmentType || '';
    const location = stop.location || {};
    const dockType = (location.dockType || stop.dockType || appointmentType || '').toUpperCase();
    const dockHoursRange = resolveDockHours(location);
    const dockHours = composeDockHoursRange(dockHoursRange.from, dockHoursRange.to);
    const locationPayload = location.id
      ? { location_id: location.id }
      : {
          location_payload: {
            company_name: location.name || location.company_name || '',
            address: location.street || location.address || '',
            city: location.city || '',
            state: location.state || '',
            zip: location.zip || '',
            dock_type: dockType || null,
            dock_hours: dockHours || location.dockHours || '',
            dock_hours_from: dockHoursRange.from,
            dock_hours_to: dockHoursRange.to,
            notes: stop.notes || location.notes || '',
          },
        };
    return {
      stop_type: (stop.type || 'pickup').toLowerCase(),
      sequence,
      window_start: combineDateTime(stop.date, stop.windowStart),
      window_end: combineDateTime(stop.date, stop.windowEnd),
      appointment_type: appointmentType || null,
      appointment_required:
        appointmentType === 'Appointment' ||
        appointmentType === 'APPOINTMENT' ||
        Boolean(stop.needsAppointmentScheduled),
      appointment_number: stop.appointmentNumber || '',
      reference_number: stop.reference || '',
      notes: stop.notes || '',
      ...locationPayload,
    };
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitError('');
    if (!billRate) {
      setBillRateError(true);
      return;
    }
    const hasIncompleteLocation = stops.some((stop) => !ensureCompleteLocation(stop));
    if (hasIncompleteLocation) {
      setSubmitError('Please provide a complete address (street, city, state, ZIP) for every stop.');
      return;
    }

    const sanitizedBillRate = billRate.replace(/[^0-9.]/g, '');
    const parsedBillRate = sanitizedBillRate ? Number.parseFloat(sanitizedBillRate) : null;

    const stopsPayload = stops.map((stop, index) => buildStopPayload(stop, index + 1));

    const lane = hasRouteContext ? `${origin.city}, ${origin.state} → ${destination.city}, ${destination.state}` : '';
    const commodityType = commoditySummary.join(' | ');

    const payload = {
      shipment_type: mode,
      po_number: refNumber || `Pending-${Date.now()}`,
      trailer_type: equipment,
      trailer_length: Number.parseFloat(length.replace(/[^0-9.]/g, '')) || null,
      load_weight: weight ? Number.parseFloat(weight.replace(/[^0-9.]/g, '')) : null,
      load_value_range: valueRange,
      commodity_type: commodityType || equipment,
      special_load_requirements: specialRequirements,
      bill_rate: parsedBillRate,
      lane,
      pickup_date: stops[0]?.date ? combineDateTime(stops[0].date, stops[0].windowStart) : null,
      delivery_date: stops[stops.length - 1]?.date
        ? combineDateTime(stops[stops.length - 1].date, stops[stops.length - 1].windowEnd)
        : null,
      truck_number: 'TBD',
      trailer_number: 'TBD',
      last_remark: 'Awaiting carrier assignment',
      stops: stopsPayload,
    };

    addBooking({
      customerRef: payload.po_number,
      mode,
      equipment: `${equipment} ${length}`,
      origin: origin ? `${origin.city}, ${origin.state}` : 'TBD',
      destination: destination ? `${destination.city}, ${destination.state}` : 'TBD',
      value: sanitizedBillRate ? `$${sanitizedBillRate}` : `$${billRate}`,
      carrierId,
      commodities: commoditySummary,
      requirements: specialRequirements,
    });

    addLoad(payload)
      .then(onClose)
      .catch(() => setSubmitError('We could not create the shipment. Please double-check the form and try again.'));
  };

  return (
    <div className={drawerStyles.backdrop}>
      <div className={drawerStyles.drawer}>
        <div className={drawerStyles.header}>
          <div>
            <h2>Create shipment</h2>
            <p>
              Design multi-stop tenders with curated carriers and equipment. Everything syncs with the load board instantly.
            </p>
          </div>
          <button type="button" className={drawerStyles.closeButton} onClick={onClose} aria-label="Close shipment drawer">
            <FiX />
          </button>
        </div>
        <div className={drawerStyles.content}>
          <div className={styles.layout}>
            <form id="shipment-form" className={styles.formColumn} onSubmit={handleSubmit}>
              <section className={styles.section}>
              <h4>Mode &amp; equipment</h4>
              <div className={styles.segmentedControl}>
                {modes.map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={[styles.segment, mode === value ? styles.active : ''].filter(Boolean).join(' ')}
                    onClick={() => setMode(value)}
                  >
                    {value}
                  </button>
                ))}
              </div>
              <div className={styles.gridTwo}>
                <label>
                  Equipment
                  <select
                    value={equipment}
                    onChange={(event) => {
                      setEquipment(event.target.value);
                      const defaults = trailerDefaults[event.target.value]?.lengths || [];
                      if (defaults.length) {
                        setLength(defaults[0]);
                      }
                      setSpecialRequirements([]);
                      setRequirementsOpen(false);
                    }}
                  >
                    {Object.keys(trailerDefaults).map((equip) => (
                      <option key={equip} value={equip}>
                        {equip}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Trailer length
                  <select value={length} onChange={(event) => setLength(event.target.value)}>
                    {lengthOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className={styles.gridThree}>
                <label>
                  Weight (lbs)
                  <input value={weight} onChange={(event) => setWeight(event.target.value)} placeholder="42,000" />
                </label>
                <label>
                  Declared value
                  <select value={valueRange} onChange={(event) => setValueRange(event.target.value)}>
                    {valueBuckets.map((bucket) => (
                      <option key={bucket} value={bucket}>
                        {bucket}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Ref / PO # (optional)
                  <input value={refNumber} onChange={(event) => setRefNumber(event.target.value)} placeholder="PO-11852" />
                </label>
              </div>
              <div className={styles.carrierRow}>
                <label>
                  Managed by
                  <select value={carrierId} onChange={(event) => setCarrierId(event.target.value)}>
                    {carriers.map((carrier) => (
                      <option key={carrier.id} value={carrier.id}>
                        {carrier.name} (MC {carrier.mc})
                      </option>
                    ))}
                  </select>
                </label>
                <label className={billRateError ? styles.errorField : ''}>
                  Bill rate (USD)
                  <input
                    value={billRate}
                    onChange={(event) => {
                      setBillRate(event.target.value);
                      setBillRateError(false);
                    }}
                    placeholder="3200"
                  />
                </label>
              </div>
              {resolveCarrier && (
                <div className={styles.carrierProfile}>
                  <h5>{resolveCarrier.name}</h5>
                  <p>MC {resolveCarrier.mc} · DOT {resolveCarrier.dot}</p>
                  <span>{resolveCarrier.specializations.join(' • ')}</span>
                </div>
              )}
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4>Commodity details</h4>
                <Button type="button" size="sm" variant="ghost" leadingIcon={<FiPlus />} onClick={handleAddCommodity}>
                  Add commodity
                </Button>
              </div>
              {commodities.length === 0 && (
                <div className={styles.emptyCommodities}>Specify items to enhance rate confirmations and carrier instructions.</div>
              )}
              <div className={styles.commodityList}>
                {commodities.map((commodity, index) => (
                  <div key={`commodity-${index}`} className={styles.commodityRow}>
                    <div className={styles.commodityGrid}>
                      <label>
                        Quantity
                        <input
                          value={commodity.quantity}
                          onChange={(event) => handleCommodityChange(index, { quantity: event.target.value })}
                          placeholder="1"
                        />
                      </label>
                      <label>
                        Units
                        <input
                          value={commodity.unit}
                          list={unitListId}
                          onChange={(event) => handleCommodityChange(index, { unit: event.target.value })}
                          onBlur={(event) => handleCommodityUnitBlur(index, event.target.value)}
                          placeholder="Truckload"
                        />
                      </label>
                    </div>
                    <label>
                      Item description
                      <input
                        value={commodity.description}
                        onChange={(event) => handleCommodityChange(index, { description: event.target.value })}
                        placeholder="Aluminum bundles"
                      />
                    </label>
                    <button
                      type="button"
                      className={styles.removeCommodity}
                      onClick={() => handleRemoveCommodity(index)}
                      aria-label="Remove commodity"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))}
              </div>
            </section>

            <section className={styles.section}>
              <h4>Special requirements</h4>
              <div className={styles.requirementsField} ref={requirementsRef}>
                <button
                  type="button"
                  className={styles.requirementsTrigger}
                  onClick={() => setRequirementsOpen((prev) => !prev)}
                >
                  {specialRequirements.length === 0 ? 'Select requirements' : `${specialRequirements.length} selected`}
                </button>
                {requirementsOpen && (
                  <div className={styles.requirementsMenu}>
                    <div className={styles.requirementsScroll}>
                      {availableRequirements.map((requirement) => (
                        <label key={requirement} className={styles.requirementOption}>
                          <input
                            type="checkbox"
                            checked={specialRequirements.includes(requirement)}
                            onChange={() => toggleRequirement(requirement)}
                          />
                          <div>
                            <span>{requirement}</span>
                            <small>{requirementDescriptions[requirement] || 'Operational note logged for carrier teams.'}</small>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className={styles.alert}>
                <FiAlertTriangle />
                <p>Need something bespoke? Add notes per stop below so our operators can confirm compliance.</p>
              </div>
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4>Stops</h4>
                <button type="button" className={styles.addStopPrimary} onClick={handleAddStop}>
                  <FiPlus /> Add stop
                </button>
              </div>
              {stops.length === 0 ? (
                <div className={styles.stopsEmpty}>
                  No stops yet. Click "Add stop" to create your first pickup or delivery.
                </div>
              ) : (
                <StopsTable
                  stops={stops}
                  onChangeStop={handleStopUpdate}
                  onReorderStops={handleReorderStops}
                  onRemoveStop={handleRemoveStop}
                  onLocationChange={handleLocationChange}
                  onTimeBlur={handleTimeBlur}
                />
              )}
            </section>

            {submitError && <div className={styles.formError}>{submitError}</div>}
          </form>

            <aside className={styles.visualColumn}>
            <div className={styles.visualCard}>
              <div className={styles.visualHeader}>
                <div>
                  <h4>Route visualization</h4>
                  <p>Google Maps rendering will appear once addresses are confirmed.</p>
                </div>
                <FiMapPin />
              </div>
              <div className={styles.visualBody}>
                {hasRouteContext ? (
                  <div className={styles.routeSummary}>
                    <span>{origin.name}</span>
                    <span className={styles.routeArrow}>→</span>
                    <span>{destination.name}</span>
                  </div>
                ) : (
                  <div className={styles.visualEmpty}>Add at least one pickup and delivery to preview the lane.</div>
                )}
                <div className={styles.visualPlaceholder}>
                  <span>Route preview placeholder</span>
                </div>
              </div>
              <footer className={styles.visualFooter}>
                Estimated transit times and heatmaps will activate automatically once DAT RateView and Google Places connect.
              </footer>
            </div>
            {commoditySummary.length > 0 && (
              <div className={styles.visualCard}>
                <div className={styles.visualHeader}>
                  <div>
                    <h4>Commodity snapshot</h4>
                    <p>Shared directly with carriers via the booking confirmation.</p>
                  </div>
                </div>
                <ul className={styles.summaryList}>
                  {commoditySummary.map((entry, index) => (
                    <li key={entry + index}>{entry}</li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
          </div>
        </div>
        <div className={drawerStyles.footer}>
          <span className={styles.footerCopy}>Shipment drafts sync with the bookings board immediately after submission.</span>
          <Button type="submit" form="shipment-form" variant="primary">
            Create shipment
          </Button>
        </div>
        <datalist id={unitListId}>
          {unitOptions.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
      </div>
    </div>
  );
};

CreateShipmentDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired
};

CreateShipmentDrawer.defaultProps = {
  open: false
};

export default CreateShipmentDrawer;
